import { CopyIcon } from 'lucide-react';
import React, { useMemo } from 'react';

import type { ThemeOption } from '@/app/ThemeContext';
import { TabOption } from '@/components/chariot-ui';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { ProofRenderer } from '@/components/proof/ProofRenderer';
import { isStructuredProof } from '@/components/proof/types';
import { Table } from '@/components/table/Table';
import { Button } from '@/components/ui/button';
import { ConstantineDemonstratedSection } from '@/sections/poe/components/ConstantineDemonstratedSection';
import { EvidenceImageGallery } from '@/sections/poe/components/EvidenceImageGallery';
import { HttpsLink } from '@/sections/poe/components/HttpsLink';
import {
  getVulnerabilityClassification,
  VulnerabilityClassification,
} from '@/sections/poe/components/VulnerabilityClassification';
import { MAIN_TAB_DEFS } from '@/sections/poe/constants/mainTabDefs';
import { processAugustusDataSource } from '@/sections/poe/processors/augustus.processor';
import { processBurpDataSource } from '@/sections/poe/processors/Burp.processor';
import { processConstantineDataSource } from '@/sections/poe/processors/constantine.processor';
import { processCrawlerDataSource } from '@/sections/poe/processors/crawler.processor';
import { renderGatoEvidence } from '@/sections/poe/processors/Gato.processor';
import { processGenericDataSource } from '@/sections/poe/processors/Generic.processor';
import { processHadrianDataSource } from '@/sections/poe/processors/hadrian.processor';
import { addLoginPanelTab } from '@/sections/poe/processors/LoginPanel.processor';
import { processNucleiDataSource } from '@/sections/poe/processors/Nuclei.processor';
import { processOrcaDataSource } from '@/sections/poe/processors/Orca.processor';
import { processPortscanDataSource } from '@/sections/poe/processors/portscan.processor';
import { processSecretsDataSource } from '@/sections/poe/processors/Secrets.processor';
import { processSnykCodeDataSource } from '@/sections/poe/processors/Snyk.processor';
import { processSshDataSource } from '@/sections/poe/processors/ssh.processor';
import { processWhoisDataSource } from '@/sections/poe/processors/whois.processor';
import { processWizDataSource } from '@/sections/poe/processors/Wiz.processor';
import type {
  ExtendedTabOption,
  GitHubPOE,
  MainTabKey,
  PatchSelection,
  POEContent,
  SectionContent,
  SummaryPOE,
} from '@/sections/poe/types/poe.types';
import {
  collectRiskImages,
  type RiskImage,
} from '@/sections/poe/utils/collectRiskImages';
import { createImageSection } from '@/sections/poe/utils/CreateImageSection';
import { isHttpsUrl } from '@/sections/poe/utils/httpsUrl';
import type { Attribute, RiskSeverity, Webpage } from '@/types';
import { formatDate } from '@/utils/date.util';
import { decode } from '@/utils/encrypt.util';

function getMainTabsForSource(source: string): MainTabKey[] {
  if (source.startsWith('nuclei') || source === 'cve-2025-55182') {
    return [
      'INFORMATION',
      'REQUEST',
      'RESPONSE',
      'DETECTION',
      'ATTRIBUTES',
      'LOGIN_PANEL',
    ];
  }
  if (source.startsWith('burp')) {
    return [
      'INFORMATION',
      'REMEDIATION',
      'REQUEST',
      'RESPONSE',
      'ATTRIBUTES',
      'LOGIN_PANEL',
    ];
  }
  if (source.startsWith('crawler')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES', 'LOGIN_PANEL'];
  }
  if (source.startsWith('login-detection') || source.startsWith('loginpanel')) {
    return ['INFORMATION', 'LOGIN_PANEL', 'ATTRIBUTES'];
  }
  if (source.startsWith('snyk')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  if (
    source.startsWith('secrets') ||
    source.includes('find-secrets') ||
    source.includes('-secrets')
  ) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  if (source.startsWith('augustus')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  if (source.startsWith('constantine')) {
    // DETECTION first: Constantine's processor only populates DETECTION with
    // findings, so INFORMATION would otherwise contain only aux-info text and
    // become an unexpected default landing tab when aux info is set.
    // No ATTRIBUTES: constantine (repository SAST) risks carry no risk
    // attributes, so the always-kept-when-listed ATTRIBUTES entry (see
    // buildAvailableTabs) rendered as a permanently blank rail tab.
    return ['DETECTION', 'DEMONSTRATED', 'INFORMATION'];
  }
  if (source.startsWith('orca')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  if (source.startsWith('hadrian')) {
    return ['INFORMATION', 'DETECTION', 'REQUEST', 'RESPONSE', 'ATTRIBUTES'];
  }
  if (source.startsWith('wiz')) {
    return ['INFORMATION', 'DETECTION', 'ATTRIBUTES'];
  }
  return ['INFORMATION', 'ATTRIBUTES', 'LOGIN_PANEL'];
}

export interface UsePOESectionsParams {
  name: string;
  source: string;
  severity: RiskSeverity | undefined;
  file: unknown;
  fileStatus: 'pending' | 'success' | 'error';
  evidenceFileValue: string;
  /** True when the evidence/{riskKey} fetch genuinely failed (usePOEData's
   * surfaceErrors query) — renders a distinct error state instead of the
   * "No verification notes" empty state. */
  evidenceLoadFailed?: boolean;
  attributes: Attribute[];
  handleCopy: (content: string) => void;
  webpage: Webpage | undefined;
  theme: 'light' | 'dark';
  riskKey?: string;
  patches?: PatchSelection[];
  /** Fallback prose for the Verification "Analysis" pane when evidence/{riskKey}
   * is absent (manual findings never write it). Pre-resolved and metadata-
   * stripped by the host drawer from the risk definition; renders under the
   * stable `verification_analysis` key in place of the empty-state card. Empty
   * string ('') means no fallback — the empty-state card is kept. ENG-5067. */
  verificationFallbackValue?: string;
}

interface UsePOESectionsResult {
  sections: Record<string, SectionContent>;
  availableTabs: ExtendedTabOption[];
  copyContents: Record<string, string>;
}

export function usePOESections({
  name,
  source,
  severity,
  file,
  fileStatus,
  evidenceFileValue,
  evidenceLoadFailed,
  attributes,
  handleCopy,
  webpage,
  theme,
  riskKey,
  patches,
  verificationFallbackValue,
}: UsePOESectionsParams): UsePOESectionsResult {
  return useMemo(() => {
    if (fileStatus === 'pending') {
      return { sections: {}, availableTabs: [], copyContents: {} };
    }

    const mainTabKeys = getMainTabsForSource(source);

    // ENG-5014: dissolve the former Claim/INFORMATION rail tab into DETECTION.
    // Processors and the inline push-sites still populate tempTabs['INFORMATION']
    // (each gated on mainTabKeys.includes('INFORMATION')), so we merge at the
    // tab-build stage rather than stripping INFORMATION from getMainTabsForSource
    // — which would orphan that content. Moves INFORMATION's sub-tabs into
    // DETECTION and remaps INFORMATION's slot in the key order to DETECTION.
    function mergeInformationIntoDetection(
      tempTabs: Record<MainTabKey, TabOption[]>,
      keys: MainTabKey[]
    ): MainTabKey[] {
      const info = tempTabs['INFORMATION'];
      if (info.length > 0) {
        const infoIdx = keys.indexOf('INFORMATION');
        const detIdx = keys.indexOf('DETECTION');
        // Preserve the source's intended stacking order: whichever tab the
        // source lists first leads inside the merged Detection pane. constantine
        // (['DETECTION','INFORMATION']) keeps its findings on top; every other
        // source leads with the former Claim content.
        const informationLeads =
          infoIdx === -1 || detIdx === -1 || infoIdx < detIdx;
        tempTabs['DETECTION'] = informationLeads
          ? [...info, ...tempTabs['DETECTION']]
          : [...tempTabs['DETECTION'], ...info];
        tempTabs['INFORMATION'] = [];
      }
      // Remap key order: INFORMATION's slot becomes DETECTION, de-duped so a
      // source already listing DETECTION doesn't render it twice.
      const seen = new Set<MainTabKey>();
      const mergedKeys: MainTabKey[] = [];
      for (const key of keys) {
        const mapped: MainTabKey = key === 'INFORMATION' ? 'DETECTION' : key;
        if (!seen.has(mapped)) {
          seen.add(mapped);
          mergedKeys.push(mapped);
        }
      }
      return mergedKeys;
    }

    function buildAvailableTabs(
      tempTabs: Record<MainTabKey, TabOption[]>,
      keys: MainTabKey[]
    ): ExtendedTabOption[] {
      // Fold Claim/INFORMATION into DETECTION and use the remapped key order.
      const mergedKeys = mergeInformationIntoDetection(tempTabs, keys);
      // IMAGES and VERIFICATION are not part of any getMainTabsForSource()
      // ordering, so surface them (when populated) regardless of source.
      // Appended last so they never displace a source's intended default
      // landing tab (availableTabs[0]).
      let effectiveKeys =
        tempTabs['IMAGES'].length > 0 && !mergedKeys.includes('IMAGES')
          ? [...mergedKeys, 'IMAGES' as MainTabKey]
          : mergedKeys;
      if (
        tempTabs['VERIFICATION'].length > 0 &&
        !effectiveKeys.includes('VERIFICATION')
      ) {
        effectiveKeys = [...effectiveKeys, 'VERIFICATION' as MainTabKey];
      }
      return effectiveKeys
        .filter(key => tempTabs[key].length > 0 || key === 'ATTRIBUTES')
        .map(key => {
          const tabDef = MAIN_TAB_DEFS[key];
          return {
            // Plain-string label + separate `icon`: POE.tsx's rail renders the
            // icon and label independently (icon-only when the rail is
            // collapsed, icon + label when expanded). Keep these split rather
            // than baking the icon into a JSX label.
            label: tabDef.label,
            icon: tabDef.icon,
            heading: tabDef.heading || tabDef.label,
            hideHeader: tabDef.hideHeader,
            value: tabDef.id,
            id: tabDef.id,
            subTabs: tempTabs[key],
          };
        }) as ExtendedTabOption[];
    }

    function buildAllTabsFromTempTabs(
      tempTabs: Record<MainTabKey, TabOption[]>
    ): ExtendedTabOption[] {
      // Fold Claim/INFORMATION into DETECTION for the early-return proof shapes
      // (structured proof, uploaded image, gato). Object-key iteration below then
      // renders DETECTION in place of the dissolved INFORMATION key.
      mergeInformationIntoDetection(tempTabs, mainTabKeys);
      return Object.keys(tempTabs)
        .filter(key => tempTabs[key as MainTabKey].length > 0)
        .map(key => {
          const tabDef = MAIN_TAB_DEFS[key as MainTabKey];
          return {
            // Plain-string label + separate `icon` (see buildAvailableTabs).
            label: tabDef.label,
            icon: tabDef.icon,
            heading: tabDef.heading || tabDef.label,
            hideHeader: tabDef.hideHeader,
            value: tabDef.id,
            id: tabDef.id,
            subTabs: tempTabs[key as MainTabKey],
          };
        }) as ExtendedTabOption[];
    }

    function getPOESections(): UsePOESectionsResult {
      try {
        const poeData = file as POEContent;
        const fileAsContent = file as POEContent | string;
        const sections: Record<string, SectionContent> = {};
        const copyContents: Record<string, string> = {};
        const tempTabs: Record<MainTabKey, TabOption[]> = {
          REQUEST: [],
          RESPONSE: [],
          DETECTION: [],
          DEMONSTRATED: [],
          ATTRIBUTES: [],
          LOGIN_PANEL: [],
          INFORMATION: [],
          REMEDIATION: [],
          IMAGES: [],
          VERIFICATION: [],
        };

        // Single classification of the evidence-notes content, shared by the
        // push branches below and the placeholder-drop carve-out after
        // processor dispatch — one source of truth so the two can't desync.
        // 'prose' = real notes text; 'error' = the evidence fetch genuinely
        // failed (usePOEData surfaceErrors query); 'empty' = no notes (a
        // blob: value is uploaded image evidence, never notes).
        const notesState =
          evidenceFileValue && !evidenceFileValue.startsWith('blob:')
            ? 'prose'
            : evidenceLoadFailed
              ? 'error'
              : 'empty';

        // Verification rail item — ALWAYS PRESENT for the risk vulnerability
        // drawer's POE view, scoped by `riskKey`. `riskKey` is present only when
        // POE is hosted by VulnerabilityDrawer (risk-POE); TechnologyEvidence and
        // any other consumer omit it, so they never gain this item. Placed before
        // every early-return path so it surfaces regardless of proof shape.
        // (One carve-out: after processor dispatch the empty-state placeholder
        // is dropped when the processor appended its own per-finding
        // verification cards — see the block after the dispatch try/catch.)
        //
        // The sub-tab value is a single STABLE 'verification_analysis' in BOTH
        // content states. POE.tsx freezes the user-selected tab object in state
        // and its stale-tab revalidation compares only TOP-LEVEL tab values, so
        // a data-dependent sub-tab value (prose vs empty) would go stale inside
        // the frozen object when a save flips empty→prose — the fresh sections
        // map would no longer contain the old key and the pane would render
        // blank. Only the CONTENT under the stable key varies.
        if (riskKey) {
          tempTabs['VERIFICATION'].push({
            label: 'Analysis',
            value: 'verification_analysis',
          });
          if (notesState === 'prose') {
            sections['verification_analysis'] = (
              <MarkdownPreview source={evidenceFileValue} />
            );
            copyContents['verification_analysis'] = evidenceFileValue;
          } else if (notesState === 'error') {
            // Distinct ERROR state (F2): the evidence fetch genuinely failed
            // (usePOEData surfaceErrors query), so '' here does NOT mean "no
            // notes" — saying so would misrepresent server state. No
            // copyContents entry, design-token JSX only, copy stays neutral.
            sections['verification_analysis'] = (
              <div className="text-chariot-text-secondary text-sm">
                <p className="text-chariot-text-primary font-medium">
                  Verification notes couldn&apos;t be loaded
                </p>
                <p className="mt-1">
                  Try again, or refresh the page to reload this finding.
                </p>
              </div>
            );
          } else if (verificationFallbackValue) {
            // Fallback (ENG-5067): manual findings never write evidence/{riskKey},
            // so notes are 'empty', but the write-up still exists in the risk
            // definition the Overview Description renders. The host drawer pre-
            // resolves and metadata-strips that prose; render it here (identical
            // to Overview) instead of the empty-state card. A later evidence save
            // flips notesState to 'prose' and takes over above — this branch is
            // the no-evidence path only. NOT applied to the 'error' state above:
            // a genuine fetch failure must never be masked as content.
            sections['verification_analysis'] = (
              <MarkdownPreview
                source={verificationFallbackValue}
                processVulnerabilityContent={true}
              />
            );
            copyContents['verification_analysis'] = verificationFallbackValue;
          } else {
            // Informational empty state — no copyContents entry (so POE.tsx's
            // getSectionContent yields no copy control), no Add/Edit button
            // (editing lives in the drawer header menu). Design-token JSX only.
            // Copy stays NEUTRAL about existence: '' also means the evidence
            // fetch is in flight or errored (useGetFile coerces errors to ''),
            // so we describe what is shown, not what has been added.
            sections['verification_analysis'] = (
              <div className="text-chariot-text-secondary text-sm">
                <p className="text-chariot-text-primary font-medium">
                  No verification notes
                </p>
                <p className="mt-1">
                  No verification notes to display for this finding.
                </p>
              </div>
            );
          }
          if (source.startsWith('constantine')) {
            tempTabs['DEMONSTRATED'].push({
              label: 'Agent Progress',
              value: 'demonstrated_progress',
            });
            sections['demonstrated_progress'] = (
              // Fresh-string copy of riskKey: capturing the hook argument
              // itself into the mutable `sections` map makes the React
              // Compiler treat later tempTabs/sections mutations as writes to
              // a frozen hook arg (react-hooks/immutability).
              <ConstantineDemonstratedSection
                riskKey={`${riskKey}`}
                attributes={attributes}
              />
            );
          }
        }

        if (isStructuredProof(file)) {
          file.sections.forEach((section, idx) => {
            const value = `structured_proof_${idx}`;
            sections[value] = (
              <ProofRenderer
                proof={{ format: file.format, sections: [section] }}
                bare
                layout="accordion"
              />
            );
            tempTabs['INFORMATION'].push({
              label: section.title || 'Evidence',
              value,
            });
          });

          const riskImages = collectRiskImages(file.sections);
          if (riskImages.length > 0) {
            sections['images'] = <EvidenceImageGallery images={riskImages} />;
            tempTabs['IMAGES'].push({ label: 'Images', value: 'images' });
          }

          return {
            sections,
            availableTabs: buildAllTabsFromTempTabs(tempTabs),
            copyContents,
          };
        }

        // Uploaded evidence images also feed the Images tab (below). They arrive
        // as already-resolved blob URLs, which EvidenceImageGallery renders
        // directly via useResolvedImageSrc.
        const uploadedImages: RiskImage[] = [];

        const evidenceImage = createImageSection(
          evidenceFileValue,
          'evidence_image',
          'Uploaded Evidence'
        );
        if (evidenceImage) {
          sections['evidence_image'] = evidenceImage.section;
          if (mainTabKeys.includes('INFORMATION')) {
            tempTabs['INFORMATION'].push(evidenceImage.tabOption);
          }
          uploadedImages.push({
            src: evidenceFileValue,
            caption: 'Uploaded Evidence',
            registryKey: 'evidence_image',
          });
        }

        const mainImage = createImageSection(
          file,
          'uploaded_image',
          'Uploaded Evidence'
        );
        if (mainImage) {
          sections['uploaded_image'] = mainImage.section;
          if (mainTabKeys.includes('INFORMATION')) {
            tempTabs['INFORMATION'].push(mainImage.tabOption);
          }
          uploadedImages.push({
            src: file as string,
            caption: 'Uploaded Evidence',
            registryKey: 'uploaded_image',
          });
        }

        if (uploadedImages.length > 0) {
          sections['images'] = <EvidenceImageGallery images={uploadedImages} />;
          tempTabs['IMAGES'].push({ label: 'Images', value: 'images' });
        }

        if (mainImage) {
          return {
            sections,
            availableTabs: buildAllTabsFromTempTabs(tempTabs),
            copyContents,
          };
        }

        if (
          source &&
          source.startsWith('gato') &&
          poeData.username &&
          poeData.enumeration &&
          Array.isArray(poeData.enumeration.repositories)
        ) {
          sections['github_evidence'] = renderGatoEvidence(
            poeData as GitHubPOE
          );
          if (tempTabs['INFORMATION']) {
            tempTabs['INFORMATION'].push({
              label: 'GitHub Evidence',
              value: 'github_evidence',
            });
          }
          return {
            sections,
            availableTabs: buildAllTabsFromTempTabs(tempTabs),
            copyContents,
          };
        }

        const summary = poeData?.info
          ? typeof poeData.info === 'string'
            ? (decode(poeData.info, {}) as SummaryPOE)
            : (poeData.info as unknown as SummaryPOE)
          : undefined;

        const vulnerabilityClassificationData = getVulnerabilityClassification(
          summary,
          severity
        );
        if (vulnerabilityClassificationData.some(({ value }) => !!value)) {
          sections['vuln_classification'] = (
            <VulnerabilityClassification
              data={vulnerabilityClassificationData}
              severity={severity}
            />
          );
          if (mainTabKeys.includes('INFORMATION')) {
            tempTabs['INFORMATION'].push({
              label: 'Vulnerability Classification',
              value: 'vuln_classification',
            });
          }
        }

        // L4: isolate processor-dispatch failures so a single bad processor
        // cannot discard the always-present VERIFICATION tab (built above)
        // or the ATTRIBUTES tab (below). `sections`/`tempTabs` are
        // mutated in place, so on a throw we log and continue to
        // buildAvailableTabs with whatever was populated before the failure.
        // The outer try/catch (below) stays as the last-resort net for the
        // early-return proof shapes that don't run these processors.
        try {
          if (source.startsWith('burp')) {
            processBurpDataSource(
              poeData,
              sections,
              tempTabs,
              mainTabKeys,
              copyContents
            );
          } else if (source.startsWith('login-detection')) {
            // Login-detection proof is rendered entirely by the Login Panel below.
          } else if (
            source.startsWith('nuclei') ||
            source === 'cve-2025-55182'
          ) {
            processNucleiDataSource(poeData, sections, tempTabs, mainTabKeys);
          } else if (
            source.startsWith('webpage-secrets') ||
            source.startsWith('crawler') ||
            source.startsWith('confluence-secrets')
          ) {
            processCrawlerDataSource(
              fileAsContent,
              sections,
              tempTabs,
              true,
              mainTabKeys
            );
          } else if (
            source.startsWith('secrets') ||
            source.includes('find-secrets') ||
            source.includes('-secrets')
          ) {
            processSecretsDataSource(fileAsContent, sections, tempTabs);
          } else if (source.startsWith('augustus')) {
            processAugustusDataSource(fileAsContent, sections, tempTabs);
          } else if (source.startsWith('constantine')) {
            processConstantineDataSource(
              fileAsContent,
              sections,
              tempTabs,
              mainTabKeys,
              copyContents,
              riskKey,
              patches
            );
          } else if (
            source.startsWith('portscan') ||
            source.startsWith('fingerprint') ||
            name.startsWith('exposure-')
          ) {
            processPortscanDataSource(poeData, sections, tempTabs, mainTabKeys);
          } else if (source.startsWith('ssh')) {
            processSshDataSource(poeData, sections, tempTabs, mainTabKeys);
          } else if (source.startsWith('whois')) {
            processWhoisDataSource(poeData, sections, tempTabs, mainTabKeys);
          } else if (source.startsWith('orca')) {
            processOrcaDataSource(poeData, sections, tempTabs, mainTabKeys);
          } else if (source.startsWith('snyk')) {
            processSnykCodeDataSource(
              fileAsContent,
              sections,
              tempTabs,
              mainTabKeys
            );
          } else if (source.startsWith('wiz')) {
            processWizDataSource(poeData, sections, tempTabs, mainTabKeys);
          } else if (source.startsWith('hadrian')) {
            processHadrianDataSource(poeData, sections, tempTabs, copyContents);
          } else {
            processGenericDataSource(
              poeData,
              sections,
              tempTabs,
              theme as ThemeOption,
              mainTabKeys,
              copyContents
            );
          }

          if (webpage && name.startsWith('web-login')) {
            addLoginPanelTab({
              sections,
              tempTabs,
              webpage,
              poeData,
              mainTabKeys,
            });
          }
        } catch (error) {
          console.error('POE processor failed; preserving other tabs:', error);
        }

        // The 'verification_analysis' evidence-notes sub-tab is pushed before
        // the processor dispatch so it is always present for risk-POE. But
        // when the processor appended its own per-finding verification cards
        // (Constantine's `${tabId}_verification`) and the notes are in the
        // informational EMPTY state, the placeholder would render "No
        // verification notes" directly above real verification analysis — a
        // contradiction. Drop the placeholder and let the finding cards stand
        // alone. The prose state (real notes) and the error state (a genuinely
        // failed evidence fetch, which '' must not misrepresent) both stay.
        // A later save flips evidenceFileValue empty→prose, re-adding the
        // sub-tab — a sub-tab SET change under an unchanged top-level value,
        // which POE.tsx's rehydration renders without a tab switch.
        // Exception (ENG-5067): when a `verificationFallbackValue` is present the
        // 'verification_analysis' sub-tab holds real fallback prose, not the
        // empty-state placeholder, so it is NOT a contradiction and must stay.
        if (
          riskKey &&
          notesState === 'empty' &&
          !verificationFallbackValue &&
          tempTabs['VERIFICATION'].length > 1
        ) {
          tempTabs['VERIFICATION'] = tempTabs['VERIFICATION'].filter(
            subTab => subTab.value !== 'verification_analysis'
          );
          delete sections['verification_analysis'];
        }

        if (
          mainTabKeys.includes('ATTRIBUTES') &&
          attributes &&
          attributes.length > 0
        ) {
          const attributesTableData = [...attributes]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(attribute => ({
              name: attribute.name,
              value: attribute.value,
              lastSeen: formatDate(attribute.visited),
            }));
          sections['attributes'] = (
            <Table
              name="attributes"
              data={attributesTableData}
              columns={[
                { label: 'Name', id: 'name' },
                {
                  label: 'Value',
                  id: 'value',
                  cell: (row: {
                    name: string;
                    value: string;
                    lastSeen: string;
                  }) =>
                    row.value ? (
                      <div className="text-chariot-text-secondary flex w-fit items-center rounded px-2 py-1">
                        {isHttpsUrl(row.value) ? (
                          <HttpsLink value={row.value} />
                        ) : (
                          <span>{row.value}</span>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Copy to clipboard"
                          onClick={() => handleCopy(row.value)}
                        >
                          <CopyIcon className="size-4" />
                        </Button>
                      </div>
                    ) : (
                      '-'
                    ),
                },
                {
                  label: 'Last Seen',
                  id: 'lastSeen',
                  align: 'right',
                  fixedWidth: 100,
                },
              ]}
              status="success"
              error={null}
              selection={{ hide: true }}
            />
          );
          const hasAttributesSubTab = tempTabs['ATTRIBUTES'].some(
            st => st.value === 'attributes'
          );
          if (!hasAttributesSubTab) {
            tempTabs['ATTRIBUTES'].push({
              label: 'Attributes',
              value: 'attributes',
            });
          }
        }

        // ENG-5069: Manual (human-created) findings — `source` ends with
        // '@praetorian.com' (the canonical manual signal, see
        // sourceDisplay.util.ts `isManual`). These hit the default catch-all
        // ordering, whose ATTRIBUTES entry is always kept for rail parity even
        // when empty (see buildAvailableTabs). A manually recorded finding
        // carries no risk attributes, so that entry rendered as a permanently
        // blank rail tab. Drop the empty ATTRIBUTES entry and — when no
        // scanner populated a detection/claim record — replace it with a
        // Detection tab that states the finding was captured by hand and
        // points to the Verification analysis. Mirrors constantine's precedent
        // (lead with DETECTION, omit the blank ATTRIBUTES tab). A manual
        // finding that DID accrue attributes keeps its populated Attributes tab.
        let effectiveMainTabKeys = mainTabKeys;
        if (
          source.endsWith('@praetorian.com') &&
          tempTabs['ATTRIBUTES'].length === 0
        ) {
          effectiveMainTabKeys = mainTabKeys.filter(
            key => key !== 'ATTRIBUTES'
          );
          if (
            tempTabs['DETECTION'].length === 0 &&
            tempTabs['INFORMATION'].length === 0
          ) {
            sections['manual_detection'] = (
              <div className="text-chariot-text-secondary text-sm">
                <p className="text-chariot-text-primary font-medium">
                  Manually documented finding
                </p>
                <p className="mt-1">
                  This finding was captured through hands-on review rather than
                  an automated scanner, so there is no programmatic detection
                  signal to display here.
                </p>
                {riskKey && (
                  <p className="mt-1">
                    Refer to the Verification tab for the recorded analysis and
                    the evidence supporting this finding.
                  </p>
                )}
              </div>
            );
            // The DETECTION tab has no explicit slot in effectiveMainTabKeys
            // (a manual source's default ordering is INFORMATION + LOGIN_PANEL);
            // it renders because buildAvailableTabs -> mergeInformationIntoDetection
            // remaps INFORMATION's slot to DETECTION. If that default ordering ever
            // drops INFORMATION, add 'DETECTION' to the manual key order here so this
            // sub-tab keeps surfacing.
            tempTabs['DETECTION'].push({
              label: 'Overview',
              value: 'manual_detection',
            });
          }
        }

        return {
          sections,
          availableTabs: buildAvailableTabs(tempTabs, effectiveMainTabKeys),
          copyContents,
        };
      } catch (error) {
        console.error('Error processing POE data:', error);
        return {
          sections: {},
          availableTabs: [] as ExtendedTabOption[],
          copyContents: {},
        };
      }
    }

    return getPOESections();
  }, [
    name,
    source,
    severity,
    file,
    fileStatus,
    evidenceFileValue,
    evidenceLoadFailed,
    attributes,
    webpage,
    theme,
    handleCopy,
    riskKey,
    patches,
    verificationFallbackValue,
  ]);
}
